import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import withAuth from '../utils/withAuth';
import fallbackAvatar from "../assets/avatar-6.png";
import { fetchAllDataAPI } from '../utils/api';
import { BASEAGENTURLS } from '../utils/BaseUrl';

/* ---------------- helpers ---------------- */
const getYoutubeId = (url) => {
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return m ? m[1] : '';
};

const sortTop = (arr) => [...arr].sort((a, b) => b.total_order_amt - a.total_order_amt).slice(0, 5);

const svgURI = (s) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(s);

const scene = (k) => {
    const bg = ['#16305E', '#4A1F2A', '#1F4A3F'][k];
    const ac = ['#D4A74A', '#F0B429', '#7FD1B0'][k];
    let b = '';
    if (k === 0) {
        for (let i = 0; i < 4; i++) for (let j = 0; j <= i; j++)
            b += `<rect x="${40 + j * 44 + (3 - i) * 22}" y="${130 - i * 34}" width="40" height="30" fill="${ac}" opacity="${0.95 - i * 0.12}"/>`;
    }
    if (k === 1) b = `<path d="M100 150L160 30L220 150z" fill="${ac}"/><rect x="154" y="64" width="12" height="46" fill="#4A1F2A"/><rect x="154" y="120" width="12" height="12" fill="#4A1F2A"/>`;
    if (k === 2) b = `<rect x="40" y="70" width="140" height="64" fill="${ac}"/><rect x="180" y="92" width="64" height="42" fill="${ac}" opacity=".8"/><circle cx="80" cy="140" r="14" fill="#0B1220" stroke="${ac}" stroke-width="5"/><circle cx="210" cy="140" r="14" fill="#0B1220" stroke="${ac}" stroke-width="5"/>`;
    return svgURI(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 160"><rect width="260" height="160" fill="${bg}"/>${b}</svg>`);
};

const POSTER = svgURI('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 90"><rect width="160" height="90" fill="#16305E"/><circle cx="80" cy="45" r="16" fill="none" stroke="#D4A74A" stroke-width="2"/><path d="M75 36v18l15-9z" fill="#D4A74A"/></svg>');

// number ko purani value se nayi value tak ginta hua dikhata hai
const CountUp = ({ value, prefix = '' }) => {
    const [shown, setShown] = useState(0);
    const from = useRef(0);
    useEffect(() => {
        const start = performance.now();
        const a = from.current;
        let raf;
        const step = (t) => {
            const p = Math.min((t - start) / 1200, 1);
            const v = Math.round(a + (value - a) * (1 - Math.pow(1 - p, 3)));
            from.current = v;
            setShown(v);
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [value]);
    return <>{prefix}{shown.toLocaleString('en-IN')}</>;
};

/* ---------------- CSS (isi file me) ---------------- */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@500;600&family=Hind:wght@400;500;600&display=swap');
.wlb-root{--navy:#0B1220;--panel:#121B2E;--line:#243252;--gold:#D4A74A;--ivory:#F3EEE2;--mute:#D2DAEC;--red:#C8102E;
  --head:'Oswald','Arial Narrow',sans-serif;--body:'Hind','Segoe UI',sans-serif;
  position:fixed;inset:0;background:var(--navy);overflow:hidden;color:var(--ivory);font-family:var(--body);z-index:9999}
.wlb-root *{box-sizing:border-box;margin:0}
.wlb-stage{width:1920px;height:1080px;position:absolute;transform-origin:0 0;display:grid;grid-template-rows:96px 1fr 84px;
  background:radial-gradient(1200px 700px at 80% -10%,#16264A 0%,var(--navy) 60%)}
.wlb-top{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;padding:0 40px;border-bottom:2px solid var(--gold)}
.wlb-logo{display:flex;align-items:center;gap:18px;font:600 38px var(--head);letter-spacing:.04em}
.wlb-logo i{width:52px;height:52px;border:2px solid var(--gold);display:grid;place-items:center;font:600 24px var(--head);color:var(--gold);font-style:normal}
.wlb-live{display:flex;align-items:center;gap:10px;background:var(--red);padding:6px 18px;font:500 24px var(--head);letter-spacing:.08em}
.wlb-live i{width:12px;height:12px;border-radius:50%;background:#fff;animation:wlbpulse 1.2s infinite}
@keyframes wlbpulse{50%{opacity:.2}}
.wlb-time{justify-self:end;text-align:right;font:500 40px var(--head);line-height:1}
.wlb-date{display:block;font:500 26px var(--body);color:var(--mute);margin-top:4px}
.wlb-main{display:grid;grid-template-columns:1100px 1fr;gap:32px;padding:28px 40px;min-height:0}
.wlb-col{display:flex;flex-direction:column;gap:24px;min-height:0}
.wlb-video{position:relative;width:1100px;height:619px;background:#000;border:1px solid var(--line);overflow:hidden}
.wlb-video img,.wlb-video iframe{position:absolute;inset:0;width:100%;height:100%;border:0}
.wlb-tag{position:absolute;left:0;bottom:0;background:var(--gold);color:var(--navy);padding:8px 24px;font:600 26px var(--head);z-index:2}
.wlb-slides{position:relative;flex:1;background:var(--panel);border:1px solid var(--line);overflow:hidden}
.wlb-slide{position:absolute;inset:0;display:flex;align-items:center;gap:28px;padding:20px 28px 20px 20px}
.wlb-slide img{width:260px;height:160px;object-fit:cover;border:1px solid var(--line)}
.wlb-st{font:600 38px var(--head);color:var(--gold);margin-bottom:8px}
.wlb-sp{font-size:28px;line-height:1.35}
.wlb-bar{position:absolute;left:0;bottom:0;height:4px;background:var(--gold);animation:wlbbar 6s linear forwards}
@keyframes wlbbar{from{width:0}to{width:100%}}

.wlb-board{background:var(--panel);border:1px solid var(--line);display:flex;flex-direction:column;min-height:0}
.wlb-bh{font:600 40px var(--head);padding:20px 28px;border-bottom:1px solid var(--line);display:flex;justify-content:space-between;align-items:baseline}
.wlb-bh span{font:500 26px var(--body);color:var(--mute)}
.wlb-rows{display:flex;flex-direction:column;flex:1;min-height:0}
.wlb-row{flex:1;display:grid;grid-template-columns:44px 88px 1fr 240px;align-items:center;gap:14px;padding:0 24px;border-bottom:1px solid var(--line);position:relative;overflow:hidden}
.wlb-row:last-child{border:0}
.wlb-rank{font:600 52px var(--head);color:var(--mute)}
.wlb-row.first{background:linear-gradient(90deg,rgba(212,167,74,.2),transparent);animation:wlbglow 3s ease-in-out infinite}
@keyframes wlbglow{50%{background:linear-gradient(90deg,rgba(212,167,74,.34),transparent)}}
.wlb-row.first .wlb-rank{color:var(--gold)}
.wlb-row.first::after{content:"";position:absolute;top:0;bottom:0;width:140px;left:-200px;background:linear-gradient(100deg,transparent,rgba(255,255,255,.14),transparent);animation:wlbshine 5s 2s infinite}
@keyframes wlbshine{to{left:110%}}
.wlb-avw{position:relative;width:88px;height:88px}
.wlb-av{width:88px;height:88px;border-radius:50%;border:3px solid var(--line);object-fit:cover;background:var(--line);display:block}
.wlb-row.first .wlb-av{border-color:var(--gold);animation:wlbring 2s ease-out infinite}
@keyframes wlbring{0%{box-shadow:0 0 0 0 rgba(212,167,74,.7)}100%{box-shadow:0 0 0 22px rgba(212,167,74,0)}}
.wlb-crown{position:absolute;top:-22px;right:-10px;font-size:34px;transform:rotate(18deg);animation:wlbcrown 2.4s ease-in-out infinite}
@keyframes wlbcrown{50%{transform:rotate(18deg) translateY(-6px)}}
.wlb-nm{font:500 34px var(--head);letter-spacing:.02em;white-space:nowrap}
.wlb-dp{font-size:24px;color:var(--mute);margin-bottom:8px}
.wlb-meter{height:8px;background:var(--line)}
.wlb-meter i{display:block;height:100%;width:0;background:var(--gold);transition:width 1.4s cubic-bezier(.2,.8,.2,1);position:relative;overflow:hidden}
.wlb-meter i::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent);transform:translateX(-100%);animation:wlbshimmer 2.4s infinite}
@keyframes wlbshimmer{to{transform:translateX(100%)}}
.wlb-sc{text-align:right;white-space:nowrap}
.wlb-amt{font:600 42px/1.1 var(--head)}
.wlb-ord{font-size:24px;color:var(--mute)}
.wlb-flash{position:absolute;inset:0;background:var(--gold);pointer-events:none}

.wlb-ticker{display:flex;background:#070C17;border-top:2px solid var(--gold);overflow:hidden}
.wlb-lb{flex:0 0 auto;background:var(--gold);color:var(--navy);display:flex;align-items:center;padding:0 36px;font:600 34px var(--head);letter-spacing:.04em;z-index:2}
.wlb-win{flex:1;overflow:hidden;display:flex;align-items:center}
.wlb-track{display:flex;white-space:nowrap;animation:wlbscroll 45s linear infinite}
.wlb-track span{font-size:34px;padding-right:90px}
.wlb-track span:before{content:"";display:inline-block;width:12px;height:12px;background:var(--gold);margin-right:24px;transform:rotate(45deg)}
@keyframes wlbscroll{to{transform:translateX(-50%)}}
.wlb-alert{position:absolute;left:40px;right:40px;bottom:120px;display:flex;align-items:stretch;background:var(--red);box-shadow:0 20px 60px rgba(0,0,0,.6);z-index:10}
.wlb-ic{width:140px;background:#fff;color:var(--red);display:grid;place-items:center;font:600 90px var(--head);animation:wlbbell 1s 4}
@keyframes wlbbell{25%{transform:rotate(-14deg)}75%{transform:rotate(14deg)}}
.wlb-tx{padding:24px 36px}
.wlb-tx span{display:block;font:500 26px var(--head);letter-spacing:.08em;opacity:.85}
.wlb-tx div{font:500 52px/1.15 var(--head);margin-top:4px}
@media (prefers-reduced-motion:reduce){.wlb-track,.wlb-row.first,.wlb-row.first::after,.wlb-row.first .wlb-av,.wlb-crown,.wlb-meter i::after{animation:none}}
`;

/* ---------------- component ---------------- */
const CenterTV = () => {
    const [now, setNow] = useState(new Date());
    const [slideIdx, setSlideIdx] = useState(0);
    const [alertText, setAlertText] = useState('');
    const [ready, setReady] = useState(false);
    const [scale, setScale] = useState(1);
    const [content, setContent] = useState({ videos: [], slides: [], announcements: [], alerts: [] });
    const [list, setList] = useState([]);

    const ids = content.videos.map(item => getYoutubeId(item.media_url || '')).filter(Boolean);
    const videoSrc = ids.length ? `https://www.youtube.com/embed/${ids[0]}?autoplay=1&mute=1&loop=1&controls=0&rel=0&playlist=${ids.join(',')}` : '';
    const maxAmt = list[0]?.total_order_amt || 1;
    const loginType = (() => {
        try {
            const tl = JSON.parse(localStorage.getItem('tldata') || 'null');
            return tl?.login_type === 'tl' ? 'tl' : 'center';
        } catch (error) {
            return 'center';
        }
    })();
    const boardCode = loginType === 'tl' ? 'TL' : 'CH';
    const boardTitle = loginType === 'tl' ? 'TL Live Board' : 'Center Head Live Board';

    const getBoardScope = () => {
        for (const key of ['centerdata', 'tldata']) {
            try {
                const value = JSON.parse(localStorage.getItem(key) || 'null');
                const centerId = Number(value?.center_id || value?.centerid || 0);
                if (centerId > 0) {
                    return {
                        center_id: centerId,
                        tl_id: value?.login_type === 'tl' ? Number(value?.center_mis_id || 0) : 0,
                        login_type: value?.login_type === 'tl' ? 'tl' : 'center',
                    };
                }
            } catch (error) {
                // Older center sessions were stored as plain text; feed remains unfiltered until next login.
            }
        }
        return { center_id: 0, tl_id: 0, login_type: '' };
    };

    useEffect(() => {
        let mounted = true;
        const loadBoard = async () => {
            const res = await fetchAllDataAPI('tv-board-feed', getBoardScope());
            if (!mounted || res?.data?.status !== 1) return;
            const data = res.data.data || {};
            const nextList = sortTop(Array.isArray(data.performers) ? data.performers.map(item => ({
                ...item,
                total_order: Number(item.total_order || 0),
                total_order_amt: Number(item.total_order_amt || 0),
            })) : []);
            setContent({
                videos: Array.isArray(data.videos) ? data.videos : [],
                slides: Array.isArray(data.slides) ? data.slides : [],
                announcements: Array.isArray(data.announcements) ? data.announcements : [],
                alerts: Array.isArray(data.alerts) ? data.alerts : [],
            });
            setList(nextList);
            setReady(false);
            setTimeout(() => mounted && setReady(true), 80);
        };
        loadBoard();
        const poll = setInterval(loadBoard, 60000);
        return () => { mounted = false; clearInterval(poll); };
    }, []);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        const r = setTimeout(() => setReady(true), 50);
        return () => { clearInterval(t); clearTimeout(r); };
    }, []);

    useEffect(() => {
        if (!content.slides.length) return undefined;
        const current = content.slides[slideIdx % content.slides.length];
        const t = setTimeout(() => setSlideIdx(i => (i + 1) % content.slides.length), Math.max(3, Number(current?.duration_seconds || 6)) * 1000);
        return () => clearTimeout(t);
    }, [content.slides, slideIdx]);

    useEffect(() => {
        let hide;
        let n = 0;
        const fire = () => {
            if (!content.alerts.length) return;
            const item = content.alerts[n++ % content.alerts.length];
            setAlertText(item.content_text || item.title || '');
            clearTimeout(hide);
            hide = setTimeout(() => setAlertText(''), Math.max(3, Number(item.duration_seconds || 8)) * 1000);
        };
        const first = setTimeout(fire, 4000);
        const loop = setInterval(fire, 35000);
        const onKey = (e) => { if (e.key === 'a' || e.key === 'A') fire(); };
        window.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(first); clearInterval(loop); clearTimeout(hide);
            window.removeEventListener('keydown', onKey);
        };
    }, [content.alerts]);

    useEffect(() => {
        const fit = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);

    const stageStyle = {
        transform: `scale(${scale})`,
        left: (window.innerWidth - 1920 * scale) / 2,
        top: (window.innerHeight - 1080 * scale) / 2,
    };
    const slide = content.slides.length ? content.slides[slideIdx % content.slides.length] : null;
    const ticker = content.announcements.map((item, i) => <span key={item.id || i}>{item.content_text || item.title}</span>);
    const performerImage = value => !value ? fallbackAvatar : (/^https?:\/\//i.test(value) ? value : BASEAGENTURLS(value));

    return (
        <div className="wlb-root">
            <style>{css}</style>
            <div className="wlb-stage" style={stageStyle}>
                <div className="wlb-top">
                    <div className="wlb-logo"><i>{boardCode}</i>{boardTitle}</div>
                    <div className="wlb-live"><i></i>Live</div>
                    <div className="wlb-time">
                        {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        <span className="wlb-date">{now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                </div>

                <div className="wlb-main">
                    <div className="wlb-col">
                        <div className="wlb-video">
                            <img src={POSTER} alt="" />
                            {videoSrc && <iframe
                                title="Aaj ka video"
                                src={videoSrc}
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />}
                            <div className="wlb-tag">Aaj ka video</div>
                        </div>

                        <div className="wlb-slides">
                            {slide ? <AnimatePresence mode="wait">
                                <motion.div
                                    key={slideIdx}
                                    className="wlb-slide"
                                    initial={{ x: 60, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: -60, opacity: 0 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <img src={slide.media_url || scene(slideIdx % 3)} alt="" />
                                    <div><div className="wlb-st">{slide.title}</div><div className="wlb-sp">{slide.content_text}</div></div>
                                </motion.div>
                            </AnimatePresence> : <div className="wlb-slide"><img src={scene(0)} alt=""/><div><div className="wlb-st">TV Board</div><div className="wlb-sp">Subadmin panel se content add karein.</div></div></div>}
                            <div className="wlb-bar" key={`bar-${slideIdx}`} />
                        </div>
                    </div>

                    <div className="wlb-board">
                        <div className="wlb-bh">Top 5 performers <span>Aaj ka score</span></div>
                        <div className="wlb-rows">
                            {list.map((p, i) => (
                                <motion.div
                                    key={p.name}
                                    layout
                                    className={`wlb-row ${i === 0 ? 'first' : ''}`}
                                    initial={{ opacity: 0, x: 140 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                        type: 'spring', stiffness: 90, damping: 16, delay: i * 0.18 + 0.3,
                                        layout: { type: 'spring', stiffness: 80, damping: 15, delay: 0 },
                                    }}
                                >
                                    <motion.div
                                        key={`rank-${i}`}
                                        className="wlb-rank"
                                        initial={{ scale: 1.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                                    >{i + 1}</motion.div>

                                    <div className="wlb-avw">
                                        <img className="wlb-av" src={performerImage(p.image)} alt={p.name} />
                                        {i === 0 && <span className="wlb-crown">👑</span>}
                                    </div>

                                    <div>
                                        <div className="wlb-nm">{p.name}</div>
                                        <div className="wlb-dp">{p.empId}</div>
                                        <div className="wlb-meter">
                                            <i style={{ width: ready ? `${Math.round((p.total_order_amt / maxAmt) * 100)}%` : 0 }} />
                                        </div>
                                    </div>

                                    <div className="wlb-sc">
                                        <div className="wlb-amt"><CountUp prefix="₹" value={p.total_order_amt} /></div>
                                        <div className="wlb-ord"><CountUp value={p.total_order} /> orders</div>
                                    </div>

                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="wlb-ticker">
                    <div className="wlb-lb">Announcement</div>
                    <div className="wlb-win"><div className="wlb-track">{ticker}{ticker}</div></div>
                </div>

                <AnimatePresence>
                    {alertText && (
                        <motion.div
                            className="wlb-alert"
                            initial={{ y: 340, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 340, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 120, damping: 16 }}
                        >
                            <div className="wlb-ic">!</div>
                            <div className="wlb-tx"><span>Zaroori suchna</span><div>{alertText}</div></div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default withAuth(CenterTV);
